<script lang="ts" setup>
import type { ContentAudio } from '~/models/Content/ContentAudio'
import type { AudioTrack } from '~/models/AudioTrack'
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { rewriteMediaURL } from '~/utils/media'
import { setTitle } from '~/utils/dom'
import useLicenseControlStore from '~/stores/licenseControl'
import useAuthStore from '~/stores/auth'
import { Subtree } from '~/models/Content/Subtree'
import { ContentType } from '~/models/Content/ContentType'
import { ColorTheme } from '~/models/Content/Color'
import { useContentHelper } from '~/composables/useContentHelper'
import { useColorTheme } from '~/composables/useColorTheme'
import { useAppColor } from '~/composables/useAppColor'
import { useContentEvent } from '~/composables/activity/useContentEvent'
import useContentApi from '~/api/contentApi'
import { rdfRestClient } from '~/api/client/rdfRestClient'
import TopBar from '~/components/utils/TopBar.vue'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import NotFound from '~/components/ResourceEngine/Components/NotFound.vue'
import AudioPlayer from '~/components/media/AudioPlayer.vue'
import ArticleNavigation from '~/components/article/ArticleNavigation.vue'

const props = defineProps<{ locationId: number }>()

const { checkItemLicense } = useLicenseControlStore()

const { findContents } = useContentApi()
const { isPlaylist } = useContentHelper()
const { isAuthenticated } = storeToRefs(useAuthStore())
const { style, theme } = useColorTheme(ColorTheme.BlueOrange)
useAppColor(theme.value[10])

const { data, isLoading } = useQuery({
  queryKey: computed(() => ['audio-page', props.locationId]),
  queryFn: async () => {
    const [content] = await findContents<ContentAudio>({
      subtreeCriterion: [Subtree.Content, Subtree.Media],
      contentTypeCriterion: [ContentType.Audio],
      locationIdCriterion: [Number(props.locationId)],
    })

    return content
  },
  staleTime: Infinity,
  retry: 1,
})

const { autoContentEvent } = useContentEvent()
autoContentEvent(data)

const { data: playlist, refetch: refetchPlaylist, isError } = useQuery<AudioTrack[]>({
  enabled: computed(() => isAuthenticated.value),
  queryKey: computed(() => ['audio-view-playlist', props.locationId, data.value?.metadata?.elementURI]),
  queryFn: async () => {
    const url = data.value?.metadata?.elementURI

    if (!url)
      return null

    return (await rdfRestClient.get(url))
      .data
      ?.results
      ?.bindings
      ?.map((item: any) => ({
        title: item?.title?.value,
        src: rewriteMediaURL(item?.url?.value),
      }))
      ?? null
  },
})
const currentTrack = ref<AudioTrack>()

watch(data, async (value) => {
  if (!value)
    return

  checkItemLicense(value)
  setTitle(value.title)

  if (isPlaylist(value)) {
    await refetchPlaylist()
    currentTrack.value = playlist.value?.[0]
  } else {
    currentTrack.value = {
      title: value.title,
      src: value.metadata?.elementURI,
    }
  }
}, { immediate: true })
</script>

<template>
  <NotFound v-if="isError" />
  <div
    v-else
    class="grid min-h-full auto-rows-max grid-rows-[1fr]"
    :style
  >
    <div
      class="font-inter grid grid-cols-[minmax(0,120ch)] grid-rows-[max-content] place-content-center gap-y-6 px-2 py-6"
      :style
    >
      <AuSkeletonWrapper v-if="isLoading">
        <AuSkeleton
          width="33%"
          height="4rem"
        />

        <AuSkeleton
          width="100%"
          height="60px"
        />
      </AuSkeletonWrapper>

      <template v-else-if="data?.metadata">
        <TopBar
          :item="data"
          :fullscreen="false"
        />
        <h1 class="text-5xl font-bold">
          {{ data.title }}
        </h1>

        <template v-if="isAuthenticated">
          <AudioPlayer
            v-if="!isPlaylist(data)"
            :src="currentTrack?.src"
            :title.attr="currentTrack?.title"
            load="eager"
          />

          <ul v-else>
            <li
              v-for="track in playlist"
              :key="track.src"
              class="mt-12"
            >
              <div class="font-bold">
                {{ track.title }}
              </div>
              <AudioPlayer
                class="mt-6 w-fit max-w-full min-w-[40rem]"
                :src="track.src"
                :title.attr="track.title"
                load="idle"
                inline
              />
            </li>
          </ul>
        </template>
      </template>
    </div>
    <ArticleNavigation
      v-if="data"
      :item="data"
    />
  </div>
</template>
